import SidePanel from '../SidePanel.jsx';
import { setClassDetailsQuery, useBookableResourcesQuery, useBusinessInfo, useClassDetailsQuery } from '../../lib/Queries';
import {
    ChevronRightIcon,
    EnvelopeIcon,
} from '@heroicons/react/24/outline/index.js';
import EventDetailsPanel from './EventDetailsPanel';
import { useEffect, useState } from 'react';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import TextInput from '../TextInput';
import BookableResourcesSelect from '../BookableResourcesSelect.jsx';
import DateTimeInput from '../DateTimeInput.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import useDelete from '../../lib/useDelete.jsx';
import DotDotDotMenu from '../DotDotDotMenu';
import { formatDateLong, formatTime, getBrowserTimezone } from '../../lib/DateTime.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import ClientPicker from '../ClientPicker.jsx';
import OptionalTimeZoneSelect from '../scheduling/OptionalTimeZoneSelect.jsx';
import EarthIcon from '../icons/EarthIcon.jsx';
import TimezoneSelect from '../TimezoneSelect.jsx';
import CustomTags from '../customfields/CustomTags.jsx';
import { ObjectTypes } from '../../lib/ObjectTypes.js';
import EditableCustomTags from '~/components/customfields/EditableCustomTags';
import Loading from '~/components/Loading';
import CustomFields from '~/components/customfields/CustomFields';
import ClassWaitlistList from '~/components/waitlist/ClassWaitlistList';
import InfoMessageText from '~/components/InfoMessageText';
import Tooltip from '~/components/Tooltip';
import WarningTriangleIcon from '~/components/icons/WarningTriangleIcon';
import * as Toast from '~/lib/Toast';
import { uniqueArray } from '~/lib/Utils';
import PaymentStatusLabel from '~/components/payments/PaymentStatusLabel';
import AdminSelectAndEditForm from '~/components/scheduling/AdminSelectAndEditForm.jsx';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import { clientFormatTime } from '~/public/lib/ClientDateTime.js';

const EditClass = ({ classData, setClassData, type }) => {
    const [hasChangedAttendees, setHasChangedAttendees] = useState(false);
    const [originalStart, setOriginalStart] = useState(classData?.start);
    const [resourceTimezone, setResourceTimezone] = useState();
    const { data: bookableResourcesData, isLoading } =
        useBookableResourcesQuery();

    useEffect(() => {
        // Find bookable_resource_id in bookableResourcesdata and set the time zone
        if (classData?.bookable_resource_id && bookableResourcesData) {
            const foundResource = bookableResourcesData.find(
                r => r.id === classData.bookable_resource_id,
            );
            setResourceTimezone(foundResource?.timezone);
        }
    }, [classData?.bookable_resource_id, bookableResourcesData]);

    const startDate = classData?.start ? new Date(classData?.start)?.toISOString() : null;

    return (
        <div className="mt-4">
            <TextInput
                id="max_attendees"
                value={classData?.max_attendees}
                label="Max Attendees"
                onChange={newValue => {
                    setClassData({ ...classData, max_attendees: newValue });
                    setHasChangedAttendees(true);
                }}
            />
            {(classData?.max_attendees === '0') && (
                <InfoMessageBlock showIcon={true} className="mt-2">
                    This will change the class to use the event type default max attendees.
                </InfoMessageBlock>
            )}
            {!!type?.is_series && hasChangedAttendees && (
                <InfoMessageBlock showIcon={true} className="mt-2">
                    Changing the max attendees will only change this one
                    instance, not the entire series.
                </InfoMessageBlock>
            )}

            <BookableResourcesSelect
                label="With"
                value={classData?.bookable_resource_id}
                onChange={(newValue, bookableResource) => {
                    setResourceTimezone(bookableResource?.timezone);
                    setClassData({
                        ...classData,
                        bookable_resource_id: newValue,
                    })
                }
                }
                className="mt-4"
            />

            <DateTimeInput
                className="mt-4"
                id="start"
                value={classData?.start}
                onChange={newValue =>
                    setClassData({ ...classData, start: newValue })
                }
            />
            {/* Warn if changing start time to the past*/}
            {classData?.start !== originalStart && new Date(classData?.start) < new Date() && (
                <ErrorMessageBlock className="mt-2">
                    This is in the past, clients will not be able to book this.
                </ErrorMessageBlock>
            )}

            {resourceTimezone !== getBrowserTimezone() && (
                <InfoMessageBlock className="mt-1" showIcon={true}>
                    {!!startDate && (
                        <div className="mb-2">
                            <strong>This will start at {clientFormatTime(startDate, resourceTimezone)}</strong><br />
                            {clientFormatTime(startDate, getBrowserTimezone())} in your time zone ({getBrowserTimezone()}) is {clientFormatTime(startDate, resourceTimezone)} in the business's time zone ({resourceTimezone}).
                        </div>
                    )}

                    All times are entered in <strong>your local time zone</strong> and automatically converted to the <strong>business's time zone</strong> after saving.
                </InfoMessageBlock>
            )}
        </div>
    );
};

const AttendeeRow = ({ attendee, isFirst, onClick }) => {
    const { data: businessData } = useBusinessInfo();
    const canPay = !!businessData?.canPay;
    return (
        <button
            className={`group py-3 pr-4 w-full flex items-center justify-between ${!isFirst ? ' border-t' : ''}`}
            type="button"
            onClick={onClick}
        >
            <div className="flex-1 text-left">
                {attendee.name}
                <EditableCustomTags
                    id={attendee.id}
                    type={ObjectTypes.BOOKING_EVENT}
                    fields={attendee.custom_fields}
                />
            </div>
            <div className="flex items-center">
                <PaymentStatusLabel
                    paymentStatus={attendee.payment_status}
                    canShowUnpaid={attendee.price > 0 && canPay}
                    className="mr-2"
                />
                <ChevronRightIcon className="w-5 h-5 text-gray-500 group-hover:text-black" />
            </div>
        </button>
    );
};

const AddAttendees = ({ addAttendee }) => {
    const [newAttendee, setNewAttendee] = useState({});

    return (
        <div className="mt-4">
            <div className="panel-label">Add Attendees</div>
            <ClientPicker
                value={newAttendee}
                setValue={client => {
                    addAttendee(client);
                }}
            />
        </div>
    );
};

const AttendeeTimeZoneInput = ({ value, onChange }) => {
    const [isChanging, setIsChanging] = useState(false);
    return (
        <div className="">
            <div className="text-sm text-gray-500">
                {value || 'No time zone'}
                <button
                    type="button"
                    className="ml-2 text-gray-500 underline hover:text-gray-600"
                    onClick={() => setIsChanging(!isChanging)}
                >
                    Change
                </button>
            </div>
            {isChanging && (
                <TimezoneSelect
                    value={value}
                    onChange={onChange}
                    inputClassName="max-w-64"
                    btnClassName="text-sm !no-underline mt-0"
                />
            )}
        </div>
    );
};

const NewAttendeeRows = ({ newAttendees, setNewAttendees, hasAttendees }) => {
    return (
        <>
            {newAttendees.map((attendee, idx) => (
                <div
                    className={`pr-4 py-3 flex items-center justify-between ${idx > 0 || hasAttendees ? 'border-t' : ''}`}
                >
                    <div>
                        <div>
                            {attendee.firstName} {attendee.lastName}
                        </div>

                        <AttendeeTimeZoneInput
                            value={attendee.clientTimezone}
                            onChange={value => {
                                const newNewAttendees = [...newAttendees];
                                newNewAttendees[idx] = {
                                    ...attendee,
                                    clientTimezone: value,
                                };
                                setNewAttendees(newNewAttendees);
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn-pink-pill"
                        onClick={() => {
                            const newNewAttendees = [...newAttendees];
                            newNewAttendees.splice(idx, 1);
                            setNewAttendees(newNewAttendees);
                        }}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </>
    );
};

const ClassDetailsPanel = ({
    selectedClass,
    isOpen,
    onClose,
    onClassUpdated,
}) => {
    const { data, isLoading, refetch } = useClassDetailsQuery(
        selectedClass?.id,
    );

    const { save, isSaving } = useBusinessPostApi(
        `/calendar/class/${selectedClass?.id}`,
    );
    const { save: saveAttendees, isSaving: isSavingAttendees } =
        useBusinessPostApi(`/calendar/class/${selectedClass?.id}/attendees`);

    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [classData, setClassData] = useState({});

    const [newAttendees, setNewAttendees] = useState([]);
    const [answers, setAnswers] = useState({});
    const [formId, setFormId] = useState(0);

    const viewClass = data?.class || selectedClass;
    const attendees = data?.attendees || [];

    const isAddingAttendees = newAttendees.length > 0;

    const { saveDelete, isDeleting } = useDelete(
        `/calendar/class/${selectedClass?.id}/delete`,
        {
            prompt:
                attendees.length > 0
                    ? `Are you sure you want to delete this class?\n\nAll ${attendees.length} attendees will be canceled and cancelation automations will be sent.`
                    : 'Are you sure you want to delete this class?',
        },
    );

    useEffect(() => {
        if (isEditing) {
            setClassData({
                max_attendees: viewClass.max_attendees,
                start: viewClass.start,
                bookable_resource_id: viewClass.bookable_resource?.id,
            });
        }
    }, [isEditing]);

    useEffect(() => {
        if (!isOpen) {
            setAnswers({});
            setIsEditing(false);
        }
    }, [isOpen]);

    function doDelete() {
        saveDelete().then(() => {
            if (onClassUpdated) onClassUpdated();
            onClose();
        });
    }

    function doSave() {
        save(classData).then(result => {
            setClassDetailsQuery(selectedClass.id, result);
            if (onClassUpdated) onClassUpdated();
            setIsEditing(false);
        });
    }

    function doEmail() {
        if (attendees.length === 0) {
            Toast.error('There are no attendees to email');
        }

        const emails = uniqueArray(attendees.map(a => a.client?.email));
        document.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(','))}`;
        Toast.success(
            'Opening your default email client with a draft to all attendees...',
        );
    }

    function doSaveAttendees() {
        saveAttendees({
            newAttendees: newAttendees,
            answers: answers,
            formId: formId,
        }).then(result => {
            setNewAttendees([]);
            setAnswers({});
            setClassDetailsQuery(selectedClass.id, result);
            if (onClassUpdated) onClassUpdated();
            setIsEditing(false);
        });
    }

    return (
        <SidePanel
            title="Class Details"
            isOpen={isOpen}
            onClose={() => {
                if (isAddingAttendees && newAttendees?.length > 0) {
                    if (
                        !confirm(
                            'You have unsaved attendees. Are you sure you want to discard them?',
                        )
                    ) {
                        // Cancel
                        return;
                    }
                }
                onClose();
            }}
            showSave={isEditing || isAddingAttendees}
            saveLabel={
                isSaving || isSavingAttendees ? 'Saving...' : 'Save Attendees'
            }
            onCancel={() => {
                setIsEditing(false);
                setNewAttendees([]);
            }}
            onSave={() => {
                if (isEditing) {
                    doSave();
                } else if (isAddingAttendees) {
                    doSaveAttendees();
                }
            }}
        >
            {!!viewClass && (
                <>
                    <div className="flex justify-between">
                        <div>
                            <h2>{viewClass.event_type?.name}</h2>
                            {!!viewClass?.settings?.label && (
                                <div>{viewClass?.settings?.label}</div>
                            )}
                            <div>with {viewClass.bookable_resource?.name}</div>
                            <div>
                                <div>
                                    {formatDateLong(viewClass.start)}{' '}
                                    {formatTime(viewClass.start)}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center">
                            {!isAddingAttendees && (
                                <button
                                    type="button"
                                    className="btn-light-circle mr-2"
                                    onClick={() => setIsEditing(!isEditing)}
                                >
                                    <PencilSquareIcon className="w-5 h-5" />
                                </button>
                            )}

                            <DotDotDotMenu
                                btnClassName="btn-light-circle"
                                options={[
                                    {
                                        name: 'Email',
                                        onClick: doEmail,
                                        icon: <EnvelopeIcon />,
                                    },
                                    {
                                        name: isDeleting
                                            ? 'Deleting..'
                                            : 'Delete',
                                        isDelete: true,
                                        onClick: doDelete,
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    {isEditing && (
                        <EditClass
                            classData={classData}
                            setClassData={setClassData}
                            type={viewClass.event_type}
                        />
                    )}

                    {!isEditing && (
                        <>
                            <CustomFields
                                id={selectedClass.id}
                                type={ObjectTypes.CLASS}
                                fields={selectedClass.custom_fields}
                                className="mt-4 "
                                onChanged={onClassUpdated}
                            />

                            {!isLoading && (
                                <div className="mt-4 mb-2 panel-label flex items-center">
                                    {attendees.length}/{viewClass.max_attendees}{' '}
                                    attendee
                                    {attendees.length !== 1 ? 's' : ''}
                                    {parseInt(viewClass.max_attendees) !==
                                        parseInt(viewClass.event_type?.settings
                                            ?.max_attendees) && (
                                        <div className="ml-2">
                                            <Tooltip
                                                tooltipId={`tooltip-class-${viewClass.id}`}
                                                message={`Max attendees is different from default (${viewClass.event_type?.settings?.max_attendees})`}
                                            >
                                                <span className="text-primary-700">
                                                    <WarningTriangleIcon className="w-4 h-4" />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="panel pl-4">
                                {isLoading && <Loading className="py-3" />}
                                {attendees.map((attendee, idx) => (
                                    <AttendeeRow
                                        key={attendee.id}
                                        attendee={attendee}
                                        isFirst={idx === 0}
                                        onClick={() =>
                                            setSelectedAttendee(attendee)
                                        }
                                    />
                                ))}
                                <NewAttendeeRows
                                    newAttendees={newAttendees}
                                    setNewAttendees={setNewAttendees}
                                    hasAttendees={attendees.length > 0}
                                />
                                {attendees.length === 0 &&
                                    newAttendees.length === 0 &&
                                    !isLoading && (
                                        <div className="py-3">
                                            There are no attendees
                                        </div>
                                    )}
                            </div>

                            <ClassWaitlistList
                                instance={selectedClass}
                                className="mt-4"
                            />

                            <EventDetailsPanel
                                event={selectedAttendee}
                                isOpen={!!selectedAttendee}
                                onClose={() => setSelectedAttendee(null)}
                                onEventChanged={() => refetch()}
                            />

                            {isAddingAttendees && newAttendees.length > 0 && (
                                <div className="mt-2">
                                    <AdminSelectAndEditForm
                                        formId={formId}
                                        setFormId={setFormId}
                                        answers={answers}
                                        setAnswers={setAnswers}
                                        eventTypeId={viewClass.event_type?.id}
                                    />
                                    {Object.keys(answers)?.length > 0 &&
                                        newAttendees.length > 1 && (
                                            <div className="text-gray-500 leading-tight text-sm mt-2">
                                                The same form answers will be
                                                used for all attendees. To add
                                                attendees with different answers
                                                please add them then save
                                                individually.
                                            </div>
                                        )}

                                    <InfoMessageBlock className="mt-2">
                                        There{' '}
                                        {newAttendees.length !== 1
                                            ? 'are'
                                            : 'is'}{' '}
                                        {newAttendees.length} unsaved attendee
                                        {newAttendees.length !== 1 ? 's' : ''}.
                                        Please Save Attendees to save them to
                                        this class.
                                    </InfoMessageBlock>
                                </div>
                            )}

                            <AddAttendees
                                addAttendee={attendee => {
                                    if (!attendee.email) {
                                        Toast.error('Attendee must have an email address.');
                                        return;
                                    }
                                    if (!attendee.firstName && !attendee.lastName) {
                                        Toast.error('Attendee must have a first or last name.');
                                        return;
                                    }
                                    setNewAttendees([
                                        ...newAttendees,
                                        attendee,
                                    ]);
                                }}
                            />
                        </>
                    )}
                </>
            )}
        </SidePanel>
    );
};

export default ClassDetailsPanel;
